import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faUsers } from "@fortawesome/pro-solid-svg-icons/faUsers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import Paragraph from "antd/es/typography/Paragraph";
import cn from "classnames";
import { useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";

import { EntityType } from "@mapmycustomers/shared";
import EmailPreview from "@mapmycustomers/shared/types/email/EmailPreview";
import EmailTemplate from "@mapmycustomers/shared/types/email/EmailTemplate";
import {
  EntitiesSupportedByEmailFeature,
  EntityTypesSupportedByEmailFeature,
} from "@mapmycustomers/shared/types/entity";
import { stopEvents } from "@mapmycustomers/shared/util/browser";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import {
  Button,
  Divider,
  Dropdown,
  EditableText,
  EmailAttachedFile,
  LoadingSpinner,
  Modal,
  Popconfirm,
} from "@mapmycustomers/ui";

import MoreButton from "@app/component/MoreButton";
import {
  deleteEmailTemplate,
  fetchEmailPreview,
  updateEmailTemplate,
} from "@app/store/email/actions";
import {
  areEmailTemplatesLoading,
  isEmailTemplateDeleting,
  isEmailTemplateUpdating,
} from "@app/store/email/selectors";
import { getCurrentUserId } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import EmailTemplateInfo from "@app/types/email/EmailTemplateInfo";
import getEmailMetadataResourcesIds from "@app/util/email/getEmailMetadataResourcesIds";
import useEmailTemplateInfoGetter from "@app/util/email/useEmailTemplateInfoGetter";
import useEntityTypeEmailTemplates from "@app/util/email/useEntityTypeEmailTemplates";

import EmailPreviewView from "../EmailPreviewView";
import EmailRecipientTypeSelect from "../EmailRecipientTypeSelect";
import EmailTemplateView from "../EmailTemplateView";

import styles from "./EmailTemplateModal.module.scss";
import messages from "./messages";
import TemplatePreviewAsModal from "./TemplatePreviewAsModal";
import TemplateShareModal from "./TemplateShareModal";
import TemplateUsers from "./TemplateUsers";

interface EmailTemplatesModalProps {
  applyLabel: ReactNode;
  initialSelectedTemplateId?: EmailTemplate["id"];
  noTemplatesActions?: ReactNode;
  onApply: (templateInfo: EmailTemplateInfo) => void;
  onHide: () => void;
  onSelectedRecipientIdChange?: (id?: EntitiesSupportedByEmailFeature["id"]) => void;
  recipients?: EntitiesSupportedByEmailFeature[];
  recipientType?: EntityTypesSupportedByEmailFeature;
  selectedRecipientId?: EntitiesSupportedByEmailFeature["id"];
  showPreviewAs?: boolean;
}

const EmailTemplatesModal = ({
  applyLabel,
  currentUserId,
  initialSelectedTemplateId,
  noTemplatesActions,
  onApply,
  onHide,
  onSelectedRecipientIdChange,
  onTemplateDelete,
  onTemplatePreviewFetch,
  onTemplateUpdate,
  recipients = [],
  recipientType: initialRecipientType,
  selectedRecipientId,
  showPreviewAs,
  templateDeleting,
  templatesLoading,
  templateUpdating,
}: EmailTemplatesModalProps & ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps) => {
  const intl = useIntl();

  const [recipientType, setRecipientType] = useState<EntityTypesSupportedByEmailFeature>(
    initialRecipientType ?? EntityType.COMPANY
  );
  const [selectedTemplateId, setSelectedTemplateId] = useState<EmailTemplate["id"] | undefined>(
    initialSelectedTemplateId
  );
  const [templateName, setTemplateName] = useState<string | undefined>();
  const [templateNameEditing, setTemplateNameEditing] = useState(false);
  const [templateNameSaving, setTemplateNameSaving] = useState(false);
  const [templatePreviewLoading, setTemplatePreviewLoading] = useState(false);
  const [templatePreview, setTemplatePreview] = useState<
    { attachedFiles: EmailAttachedFile[]; preview: EmailPreview } | undefined
  >();
  const [previewAsModalVisible, showPreviewAsModal, hidePreviewAsModal] = useBoolean();
  const [shareModalVisible, showShareModal, hideShareModal] = useBoolean();

  const templates = useEntityTypeEmailTemplates(recipientType);
  const templateInfoGetter = useEmailTemplateInfoGetter();

  const selectedTemplateInfo = selectedTemplateId
    ? templateInfoGetter(selectedTemplateId)
    : undefined;

  const selectedRecipient = useMemo(
    () =>
      selectedRecipientId
        ? recipients.find((recipient) => recipient.id === selectedRecipientId)
        : undefined,
    [recipients, selectedRecipientId]
  );

  useEffect(() => {
    if (templates.length) {
      if (
        !selectedTemplateId ||
        !templates.find((template) => template.id === selectedTemplateId)
      ) {
        setSelectedTemplateId(templates[0].id);
      }
    } else {
      setSelectedTemplateId(undefined);
    }
  }, [selectedTemplateId, templates, initialSelectedTemplateId]);

  useEffect(() => {
    setTemplateNameEditing(false);
    setTemplateName(selectedTemplateInfo?.template.name);
  }, [selectedTemplateInfo]);

  useEffect(() => {
    if (selectedRecipientId && selectedTemplateInfo?.valid && recipientType) {
      const previewTemplateId = selectedTemplateId;
      const templateInfo = selectedTemplateInfo;
      setTemplatePreviewLoading(true);
      onTemplatePreviewFetch({
        callback: (preview) => {
          if (selectedTemplateId === previewTemplateId) {
            setTemplatePreview({
              attachedFiles: (templateInfo.template.attachments ?? []).map((file) => ({
                id: uuidv4(),
                file,
              })),
              preview: preview,
            });
            setTemplatePreviewLoading(false);
          }
        },
        email: {
          body: selectedTemplateInfo.template.body,
          entityIds: [selectedRecipientId],
          entityType: recipientType,
          isHtml: true,
          logActivity: false,
          resourceIds: getEmailMetadataResourcesIds(selectedTemplateInfo.template),
          subject: selectedTemplateInfo.template.subject,
          tracking: true,
        },
        failureCallback: () => {
          if (selectedTemplateId === previewTemplateId) {
            setTemplatePreviewLoading(false);
          }
        },
      });
    } else {
      setTemplatePreview(undefined);
      setTemplatePreviewLoading(false);
    }
  }, [
    recipientType,
    onTemplatePreviewFetch,
    selectedRecipientId,
    selectedTemplateId,
    selectedTemplateInfo,
  ]);

  useEffect(() => {
    if (recipients.length) {
      onSelectedRecipientIdChange?.(recipients[0]?.id);
    }
  }, [onSelectedRecipientIdChange, recipients]);

  const handleDelete = useDynamicCallback(() => {
    if (selectedTemplateId) {
      const selectedTemplateIndex = templates.findIndex(({ id }) => id === selectedTemplateId);
      const newSelectedTemplateId = templates.length
        ? selectedTemplateIndex
          ? selectedTemplateIndex - 1
          : 0
        : undefined;
      onTemplateDelete({
        callback: () => setSelectedTemplateId(newSelectedTemplateId),
        templateId: selectedTemplateId,
      });
    }
  });

  const handleRename = useDynamicCallback((name: string) => {
    if (selectedTemplateInfo) {
      setTemplateName(name);
      setTemplateNameSaving(true);
      onTemplateUpdate({
        finishCallback: () => setTemplateNameSaving(false),
        template: { ...selectedTemplateInfo.template, name },
      });
    }
  });

  const handleExitPreview = useDynamicCallback(() => {
    onSelectedRecipientIdChange?.();
    setTemplatePreview(undefined);
  });

  const handleApply = useCallback(() => {
    if (selectedTemplateInfo) {
      onApply(selectedTemplateInfo);
    }
  }, [onApply, selectedTemplateInfo]);

  const renderTemplateView = () => {
    if (templatePreviewLoading) {
      return <LoadingSpinner />;
    }

    if (templatePreview && selectedRecipient) {
      return (
        <EmailPreviewView
          attachedFiles={templatePreview.attachedFiles}
          body={templatePreview.preview.html.body}
          recipient={selectedRecipient}
          subject={templatePreview.preview.html.subject}
        />
      );
    }

    if (selectedTemplateId) {
      return <EmailTemplateView recipients={recipients} templateId={selectedTemplateId} />;
    }

    return null;
  };

  const actionItems = useMemo(() => {
    const shareDisabled = selectedTemplateInfo?.template.userId !== currentUserId;
    const previewAsDisabled = !recipients.length;
    return [
      ...(templatePreview
        ? [
            {
              key: "exitPreview",
              label: intl.formatMessage(messages.exitPreview),
              onClick: handleExitPreview,
            },
          ]
        : []),
      ...(showPreviewAs && selectedTemplateInfo?.valid
        ? [
            {
              disabled: previewAsDisabled,
              key: "previewAs",
              label: (
                <Tooltip
                  title={
                    previewAsDisabled
                      ? intl.formatMessage(messages.previewAsDisabledTooltip)
                      : undefined
                  }
                >
                  {intl.formatMessage(messages.previewAs)}
                </Tooltip>
              ),
              onClick: showPreviewAsModal,
            },
          ]
        : []),
      {
        key: "rename",
        label: intl.formatMessage(messages.rename),
        onClick: () => setTemplateNameEditing(true),
      },
      {
        disabled: shareDisabled,
        key: "share",
        label: (
          <Tooltip
            title={
              shareDisabled ? intl.formatMessage(messages.shareDisabledDescription) : undefined
            }
          >
            <span>{intl.formatMessage(messages.share)}</span>
          </Tooltip>
        ),
        onClick: showShareModal,
      },
      {
        key: "delete",
        label: (
          <Popconfirm
            cancelText={intl.formatMessage(messages.deleteConfirmationCancel)}
            danger
            description={intl.formatMessage(messages.deleteConfirmationText)}
            okButtonProps={{ disabled: templateDeleting, loading: templateDeleting }}
            okText={intl.formatMessage(messages.deleteConfirmationOk)}
            onConfirm={handleDelete}
            overlayClassName={styles.confirmation}
            placement="left"
            title
          >
            <span onClick={stopEvents}>{intl.formatMessage(messages.delete)}</span>
          </Popconfirm>
        ),
      },
    ];
  }, [
    currentUserId,
    handleDelete,
    handleExitPreview,
    intl,
    recipients,
    selectedTemplateInfo,
    showPreviewAs,
    showPreviewAsModal,
    showShareModal,
    templateDeleting,
    templatePreview,
  ]);

  const renderActionItemsDropdown = () => {
    return (
      <Dropdown
        menu={{ items: actionItems }}
        overlayClassName={styles.templateMenu}
        placement="bottomRight"
      >
        <MoreButton horizontal type="control" />
      </Dropdown>
    );
  };

  const renderNoTemplates = () => {
    return (
      <div className={styles.noTemplatesContainer}>
        <div className={styles.noTemplatesTitle}>
          {intl.formatMessage(messages.noTemplatesTitle)}
        </div>
        <div className={styles.noTemplatesDescription}>
          {intl.formatMessage(messages.noTemplatesDescription, {
            br: <br />,
          })}
        </div>
        {noTemplatesActions}
      </div>
    );
  };

  const renderTemplatesList = () => {
    return (
      <div className={styles.templateList}>
        {templates.map((template) => (
          <div
            className={cn(styles.templateListItem, {
              [styles.templateSelected]: template.id === selectedTemplateId,
            })}
            key={template.id}
            onClick={() => setSelectedTemplateId(template.id)}
          >
            <Paragraph
              className={styles.templateListItemName}
              ellipsis={{
                tooltip: {
                  placement: "left",
                  title: template.name,
                },
              }}
            >
              {template.name}
            </Paragraph>
            {template.userIds.length > 0 && (
              <span className={styles.templateListItemSharedIcon}>
                <FontAwesomeIcon className={styles.templateSharedIcon} icon={faUsers} />
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderSelectedTemplate = () => {
    if (!selectedTemplateInfo) {
      return null;
    }

    return (
      <div className={styles.selectedTemplate}>
        {templatePreview && (
          <div className={styles.templatePreviewContainer}>
            <Tag className={styles.templateTag}>
              {intl.formatMessage(messages.previewModeTagLabel)}
            </Tag>
            {renderActionItemsDropdown()}
          </div>
        )}
        <div className={styles.templateHeader}>
          <EditableText
            className={styles.templateNameEditableText}
            editable={!templateUpdating}
            editing={templateNameEditing}
            onChange={handleRename}
            onEditingChange={setTemplateNameEditing}
            required
            saving={templateNameSaving}
            size="default"
            value={templateName}
          />
          {!templatePreview && renderActionItemsDropdown()}
        </div>
        <TemplateUsers template={selectedTemplateInfo.template} />
        <div className={styles.templateContent}>{renderTemplateView()}</div>
      </div>
    );
  };

  const renderContent = () => {
    if (templatesLoading && !templates.length) {
      return <LoadingSpinner global />;
    }

    if (!templates.length) {
      return renderNoTemplates();
    }

    return (
      <>
        {renderTemplatesList()}
        <Divider className={styles.columnsDivider} type="vertical" />
        {renderSelectedTemplate()}
      </>
    );
  };

  return (
    <>
      <Modal
        centered
        className={styles.modal}
        footer={
          <Button
            disabled={!selectedTemplateId || templateUpdating || templateDeleting}
            onClick={handleApply}
            type="primary"
          >
            {applyLabel}
          </Button>
        }
        onCancel={onHide}
        open
        scrollable
        title={
          <>
            <span>{intl.formatMessage(messages.emailTemplates)}</span>
            <EmailRecipientTypeSelect
              disabled={!!initialRecipientType}
              onChange={setRecipientType}
              value={recipientType}
            />
          </>
        }
        width="clamp(300px, 80vw, 950px)"
      >
        <div className={styles.container}>{renderContent()}</div>
      </Modal>
      {previewAsModalVisible && (
        <TemplatePreviewAsModal
          onConfirm={onSelectedRecipientIdChange}
          onHide={hidePreviewAsModal}
          recipients={recipients}
          selectedRecipientId={selectedRecipientId}
        />
      )}
      {shareModalVisible && selectedTemplateInfo && (
        <TemplateShareModal onHide={hideShareModal} template={selectedTemplateInfo.template} />
      )}
    </>
  );
};

const mapStateToProps = (state: RootState) => ({
  currentUserId: getCurrentUserId(state)!,
  templateDeleting: isEmailTemplateDeleting(state),
  templatesLoading: areEmailTemplatesLoading(state),
  templateUpdating: isEmailTemplateUpdating(state),
});

const mapDispatchToProps = {
  onTemplateDelete: deleteEmailTemplate.request,
  onTemplatePreviewFetch: fetchEmailPreview,
  onTemplateUpdate: updateEmailTemplate.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(EmailTemplatesModal);
